'use client'
import type { FormFieldBlock, Form as FormType } from '@payloadcms/plugin-form-builder/types'

import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import RichTextForm from '@/components/RichText/RichTextForm'
import { Button } from '@/components/ui/button'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

import { fields } from './fields'
import { getClientSideURL } from '@/utilities/getURL'
import { tryParseJson } from '@/utilities/tryParseJson'
import { HONEYPOT_FIELD } from './antiSpamShared'
import { TurnstileWidget } from './TurnstileWidget.client'

export type FormBlockType = {
  blockName?: string
  blockType?: 'formBlock'
  enableIntro: boolean
  form: FormType
  introContent?: DefaultTypedEditorState
}

export const FormBlock: React.FC<
  {
    id?: string
  } & FormBlockType
> = (props) => {
  const {
    enableIntro,
    form: formFromProps,
    form: { id: formID, confirmationMessage, confirmationType, redirect, submitButtonLabel } = {},
    introContent,
  } = props

  const formMethods = useForm({
    defaultValues: formFromProps.fields,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
  } = formMethods

  const [isLoading, setIsLoading] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState<boolean>()
  const [error, setError] = useState<{ message: string; status?: string } | undefined>()
  const router = useRouter()
  const t = useTranslations('Forms')

  // Anti-spam (spec CORRECAO_FORMULARIO_ANTISPAM_TURNSTILE): honeypot + rate-limit são sempre ativos no
  // servidor. O Turnstile só entra se o dono ativou no admin (global formSecurity). A Site Key (pública)
  // vem do endpoint /api/form-turnstile-config → sem env, sem rebuild. Sem ela, o form funciona igual.
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    fetch(`${getClientSideURL()}/api/form-turnstile-config`)
      .then((r) => r.json())
      .then((c: { active?: boolean; siteKey?: string }) => {
        if (alive && c?.active && c?.siteKey) setTurnstileSiteKey(c.siteKey)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  const onSubmit = useCallback(
    (data: FormFieldBlock[]) => {
      let loadingTimerID: ReturnType<typeof setTimeout>
      const submitForm = async () => {
        setError(undefined)

        const dataToSend = Object.entries(data).map(([name, value]) => ({
          field: name,
          value,
        }))

        // delay loading indicator by 1s
        loadingTimerID = setTimeout(() => {
          setIsLoading(true)
        }, 1000)

        try {
          const req = await fetch(`${getClientSideURL()}/api/form-submissions`, {
            body: JSON.stringify({
              form: formID,
              submissionData: dataToSend,
            }),
            headers: {
              'Content-Type': 'application/json',
              // Token do Turnstile via header (não vira campo/linha no banco); verificado no servidor.
              ...(turnstileToken ? { 'x-turnstile-token': turnstileToken } : {}),
            },
            method: 'POST',
          })

          const bodyText = await req.text()
          const res =
            tryParseJson<{ errors?: { message?: string }[]; status?: string }>(bodyText) ?? {}

          clearTimeout(loadingTimerID)

          if (req.status >= 400) {
            setIsLoading(false)

            setError({
              message: res.errors?.[0]?.message || t('errorInternalServer'),
              status: String(req.status),
            })

            return
          }

          setIsLoading(false)
          setHasSubmitted(true)

          if (confirmationType === 'redirect' && redirect) {
            const { url } = redirect

            const redirectUrl = url

            if (redirectUrl) router.push(redirectUrl)
          }
        } catch (err) {
          console.warn(err)
          setIsLoading(false)
          setError({
            message: t('errorGeneric'),
          })
        }
      }

      void submitForm()
    },
    [router, formID, redirect, confirmationType, t, turnstileToken],
  )

  return (
    <div className="container lg:max-w-[48rem]">
      {enableIntro && introContent && !hasSubmitted && (
        <RichTextForm className="mb-8 lg:mb-12" data={introContent} enableGutter={false} />
      )}
      <div className="p-4 lg:p-6 border border-border rounded-[0.8rem]">
        <FormProvider {...formMethods}>
          {!isLoading && hasSubmitted && confirmationType === 'message' && (
            <RichTextForm data={confirmationMessage} />
          )}
          {isLoading && !hasSubmitted && <p>{t('loading')}</p>}
          {error && <div>{`${error.status || '500'}: ${error.message || ''}`}</div>}
          {!hasSubmitted && (
            <form id={formID} onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4 last:mb-0">
                {formFromProps &&
                  formFromProps.fields &&
                  formFromProps.fields?.map((field, index) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const Field: React.FC<any> = fields?.[field.blockType as keyof typeof fields]
                    if (Field) {
                      return (
                        <div className="mb-6 last:mb-0" key={index}>
                          <Field
                            form={formFromProps}
                            {...field}
                            {...formMethods}
                            control={control}
                            errors={errors}
                            register={register}
                          />
                        </div>
                      )
                    }
                    return null
                  })}
              </div>

              {/* Honeypot: campo oculto p/ humanos, iscas p/ bots que preenchem tudo. Verificado+removido no servidor. */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                // `as never`: o register aqui é tipado contra os campos do form; o honeypot não está nesse
                // union (é injetado). Os componentes de campo contornam recebendo register como FieldValues.
                {...register(HONEYPOT_FIELD as never)}
                style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
              />

              {turnstileSiteKey && (
                <TurnstileWidget siteKey={turnstileSiteKey} onToken={setTurnstileToken} />
              )}

              <Button
                form={formID}
                type="submit"
                variant="default"
                disabled={isLoading || (Boolean(turnstileSiteKey) && !turnstileToken)}
              >
                {submitButtonLabel}
              </Button>
            </form>
          )}
        </FormProvider>
      </div>
    </div>
  )
}
